/**
 * @file LandingView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import "./PatientHome.css"
import { HomeHeaderView } from "../../../headers/views/HomeHeaderView";
import { Navigate } from "react-router-dom";
import { HomeFooterView } from "../../../footers/views/HomeFooterView";
import { Dialog, DialogContent } from "@mui/material";
import { HomeClinicView } from "./HomeClinicView";
import { Clinic } from "../../../../services/models/Clinic";
import { BaseComponent } from "../../base/BaseComponent";
import { AdminViewController } from "../../admin/controllers/AdminViewController";
import { LandingViewModel } from "../../LandingViewModel";
import { GetUserDetailResponse } from "../../../../services/models/GetUserDetailResponse";
import { ApiError } from "../../../../services/models/ApiError";
import { RoleType } from "../../../login/utils/RoleType";
import ConsentDialog from "../../../common/customviews/ConsentDialog";
import { DialogTransition } from "../../../common/customviews/DialogTransition";
import { PatientViewController } from "../controllers/PatientViewController";
import { SessionEndDialog } from "../../../common/customviews/SessionEndDialog";
import { ConsentType } from "../../../login/utils/ConsentType";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";

/**
 * A type of Landing state
 */
enum LandingState {
   Admin, Patient
}

/**
 * A type of LogoutState
 */
enum LogoutState {
   UserTrigger, TimerTrigger
}

/**
 * A type of Component state
 */
type CompState = { logOut: boolean, isClinicOpen: boolean, clinic: Clinic,
    landingState: LandingState, enableClinicSelection: boolean, firstName: string, lastName: string,
    showTerms: boolean, showPrivacy: boolean, sessionEnded: boolean, mainLoadding: boolean,
    logoutState: LogoutState
   }


interface Props {
      viewModel: LandingViewModel;
   }

/**
 * A class definition for HomeClinicView
 */   
export class LandingView extends BaseComponent<Props, CompState> {

   clinices: Clinic[] = [];
   expiredTokenTimerPoll;
   sessionTokenTimerPoll;
   constructor(props: Props) {
      super(props)
      this.expiredTokenTimerPoll = null; this.sessionTokenTimerPoll = null;
      const clinic = this.getSelectedClinic(); 
      let selectedClinic: Clinic = new Clinic();

      if(!this.isEmpty(clinic)){
         this.logger.verbose(`${clinic} - Clinic`, LandingView.name);
         selectedClinic = JSON.parse(clinic) as Clinic; // eslint-disable-line
         this.logger.verbose(`Clinics ${JSON.stringify(selectedClinic)}`, LandingView.name);
      }

      const isLoggedIn = this.isLoggedIn();
      
      this.state = {
         logOut: !isLoggedIn, firstName : 'N', lastName: "A",
         clinic: selectedClinic, enableClinicSelection : true,
         isClinicOpen : false, landingState : LandingState.Patient,
         showTerms: false, showPrivacy : false, sessionEnded: false, 
         mainLoadding: false, logoutState : LogoutState.TimerTrigger
      }
      
      this.clinices = this.isJson(this.getAllClinics()) ? JSON.parse(this.getAllClinics()!!) as Clinic[] : []; // eslint-disable-line
   }


   childPatientControllerRef: any; // estling-disable-line

   /**
    * Logs out a user
    */
   public logoutUser = () => {
      this.logger.verbose("logoutUser()", LandingView.name);
      this.setState({ logoutState: LogoutState.UserTrigger });
      this.postLogoutTriggered();
   }

   /**
    * Opens a settings
    * @returns 
    */
   public openSettings = () => {
      this.logger.verbose("openSettings()", LandingView.name);
      if(this.state.landingState === LandingState.Admin){
         return;
      }

      this.setState({
         landingState: LandingState.Admin
      });
   }

   /**
    * Triger when session dialog is dismissed.
    */
   private onSessionEndedDialogDismiss = ()=> {
      this.setState({
         sessionEnded: false, logOut: true
      });
   }

   /**
    * Poll session timers
    */
   private pollSessionChangesTimer = () => {
      //save the interval Id
      this.expiredTokenTimerPoll = setInterval(() => { if(!this.getIsLoggedIn()) {  this.setState({ logoutState: LogoutState.TimerTrigger }); this.postLogoutTriggered(); } }, 1500);
      //save the interval Id
      this.sessionTokenTimerPoll = setInterval(() => { this.setState({ logoutState: LogoutState.TimerTrigger }); this.postLogoutTriggered(); }, 60*60*1000 );
      
    }   

    /**
     * Calls post logout
     */
    private postLogoutTriggered = ()=>{
      this.clearTimer();
      this.toggleMainLoading(true);
      const sessionToken = this.getSesionToken();

      if(this.isEmpty(sessionToken)){
          return;
      }
      this.props.viewModel.logout(sessionToken.accessToken);
    }

    /**
     * Handles session
     */
    private handleSessionEnd = () => {
      this.logOut();
      if(this.state.logoutState === LogoutState.UserTrigger) { //eslint-disable-line
         this.setState({
            mainLoadding: false,
            logOut: true
         });
         return;
      }
      
      this.setState({
         mainLoadding: false,
         sessionEnded: true
      });
    }

    /**
     * Clears the timers
     */
    private clearTimer = ()=>{
      if(this.expiredTokenTimerPoll) {
         clearInterval(this.expiredTokenTimerPoll);
      }
      if(this.sessionTokenTimerPoll) {
         clearInterval(this.sessionTokenTimerPoll);
      }
    }

    /**
    * Opens home
    * @returns 
    */
   public openHome = () => {
      this.logger.verbose("openHome()", LandingView.name);
      if(this.state.landingState === LandingState.Patient)
         return;
   
      this.setState({
         landingState: LandingState.Patient, enableClinicSelection : true
      });
   }

   async componentDidMount(): Promise<void> {
      this.created();
      this.pollSessionChangesTimer();
   }

    /**
     * Toggle main loading
     */
    private toggleMainLoading = (loading: boolean) => {
      this.setState({ mainLoadding: loading });
   }

    /**
     * Triggers on component mount
     */
    private created = async () => {
      const sessionToken = this.getSesionToken();

      if(this.isEmpty(sessionToken)){
          return;
      }

      if (this.isLoggedIn()) {
         window.history.pushState(null, '', window.location.href);
         window.onpopstate = () => {
            if (this.state.showPrivacy || this.state.showTerms) {
               this.hideConsentDialog();
            } else if(this.state.landingState === LandingState.Admin) {
               this.openHome();
            }
            window.history.forward();
         };
      }

      this.props.viewModel.onUserLogout.subscribe((logoutResponse: any)=> {
         this.logger.error(logoutResponse!!, LandingView.name); // eslint-disable-line
         this.handleSessionEnd();
      });

      this.props.viewModel.onUserLogoutError.subscribe((apiError: ApiError)=> {
         // Ignore Logout errors
         this.logger.error(apiError.message!!, LandingView.name); // eslint-disable-line
         this.handleSessionEnd();
      });

      this.props.viewModel.onLoadUser.subscribe((user: GetUserDetailResponse)=> {

         if(this.getLoggedInUserRole().includes(RoleType.BSCRep)){
            this.setFirstName(user.bscRep.firstName);
            this.setLastName(user.bscRep.lastName);
            this.setUserDetails({
               email: user.bscRep.email,
               firstName : user.bscRep.firstName,
               lastName : user.bscRep.lastName,
            });
         } else {
            this.setFirstName(user.hcp.firstName);
            this.setLastName(user.hcp.lastName);
            this.setUserDetails({
               email: user.hcp.email,
               firstName : user.hcp.firstName,
               lastName : user.hcp.lastName,
            });
         }
       });

      this.props.viewModel.onLoadUserError.subscribe( (apiError: ApiError)=>{
        this.logger.error(apiError.message!!, LandingView.name); // eslint-disable-line
       });

      this.props.viewModel.getUserDetails(sessionToken.accessToken); // eslint-disable-line
   }
 
   /**
     * Sets firstName
     * @param firstName string
     */
   private setFirstName = (firstName: string) => {
      this.setState({ firstName: firstName })
   } 

   /**
     * Sets lastName
     * @param lastName string
     */
   private setLastName = (lastName: string) => {
         this.setState({ lastName: lastName })
   }

   /**
    * Shows more
    */
   public showMore = () => this.setState({ isClinicOpen: true });

   /**
    * Trigger on selection of clinic
    * @param clinic Clinic
    */
   public onClinicSelected = (clinic: Clinic) => {
      this.logger.verbose(`onClinicSelected(${JSON.stringify(clinic)})`, LandingView.name);
      this.setState({
         isClinicOpen: false,
         clinic: clinic
      });
      this.setSelectedClinic(clinic);
      this.childPatientControllerRef.onClinicSelected(clinic);
  }

  componentWillUnmount(): void {
    this.clearTimer();
  }
  /**
   * Dismisses the reset view
   * @returns 
   */
  public dismissResetView = ()=> this.setState({ isClinicOpen: false });

  /**
   * Handles QR close
   * @returns 
   */
  public handleQRClose = ()=> this.setState({ isClinicOpen: false });

  /**
   * Triggers on disabling the clinic slection
   * @returns 
   */
  public onDisabledClinicSelection = ()=> this.setState({ enableClinicSelection : false });

  /**
   * Shows privacy
   * @returns showPrivacy
   */
  public showPrivacyNotice = ()=>  this.setState({ showPrivacy : true });

  /**
   * Shows terms of use
   */
  public showTermsOfUse = ()=>  this.setState({ showTerms : true });

  /**
   * Hides consent dialog
   */
  public hideConsentDialog = ()=>  this.setState({ showPrivacy : false, showTerms : false });

  /**
   * Triggers on enabling the clinic slection
   * @returns 
   */
  public onEnabledClinicSelection = () => this.setState({ enableClinicSelection : true });
  
   render() {
      return <>
         <div style={{filter: this.state.sessionEnded? "blur(10px)" : "blur(0px)"}}>
            {this.state.logOut && <Navigate to="/" replace={true}></Navigate>}
               <HomeHeaderView logoutUser={this.logoutUser} showMore={this.showMore} selectedClinicName={this.state.clinic?.name? this.state.clinic?.name : ""}
                clinics={this.clinices} openSetting= {this.openSettings} openHome= {this.openHome} enableClinicSelection = {this.state.enableClinicSelection}
                firstName = {this.state.firstName} lastName = {this.state.lastName}/>
               <div className="HomeOuterBox">
                  { this.state.landingState === LandingState.Patient && <PatientViewController ref= {(childRef)=> this.childPatientControllerRef= childRef}/>}
                  { this.state.landingState === LandingState.Admin && <AdminViewController ref= {(childRef)=> this.childPatientControllerRef= childRef}
                                                                                             onDisabledClinicSelection={this.onDisabledClinicSelection} onEnabledClinicSelection={this.onEnabledClinicSelection}/>}
               </div>
               <BackDropLoadingView handleBackDropClose={()=>{
                  this.toggleMainLoading(false);
               }} isLoading={this.state.mainLoadding} />
               
               <HomeFooterView showPrivacyNotice={this.showPrivacyNotice} showTermsOfUse={this.showTermsOfUse}/>
                  <Dialog className="ConsentContainer" open={this.state.showPrivacy}
                        fullScreen TransitionComponent={DialogTransition}>
                           <DialogContent sx={{padding: "0px"}}>
                              <ConsentDialog hidePrivacyNotice= {this.hideConsentDialog} consentType={ConsentType.Privacy} />
                           </DialogContent>
                  </Dialog>

                  <Dialog  open={this.state.showTerms}
                           fullScreen TransitionComponent={DialogTransition}>
                           <DialogContent sx={{padding: "0px"}}>
                              <ConsentDialog hidePrivacyNotice= {this.hideConsentDialog} consentType={ConsentType.Terms}/>
                           </DialogContent>
                  </Dialog>
                     <Dialog  open={this.state.sessionEnded}
                                   aria-labelledby="customized-dialog-title"
                                   TransitionComponent={DialogTransition}>
                           <DialogContent sx={{padding: "0px"}}>
                              <SessionEndDialog onSessionDialogDismissed= {this.onSessionEndedDialogDismiss}/>
                           </DialogContent>
                  </Dialog>                
               {this.state.isClinicOpen && <Dialog onClose={this.handleQRClose} aria-labelledby="customized-dialog-title" open={this.state.isClinicOpen}
                                   maxWidth="sm" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                                   <DialogContent dividers style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                                   <HomeClinicView onClinicSelected={this.onClinicSelected} dismissResetView={this.dismissResetView} selectedClinic= {this.state.clinic}/>
                                   </DialogContent>
                               </Dialog>
               }
         </div>
      </>
   }
}